import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Layout } from "../components/Layout";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { clear } = useCart();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");
  const [kind, setKind] = useState("");
  const [downloads, setDownloads] = useState([]);
  const polls = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setStatus("error"); return; }
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        setKind(data.kind);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (data.downloads?.length) setDownloads(data.downloads);
          else if (data.download_url) setDownloads([{ url: data.download_url, name: data.product_name }]);
          if (!cleared.current) { cleared.current = true; if (data.kind === "cart" || data.kind === "product" || data.kind === "digital") clear(); refresh(); }
          return;
        }
        if (["expired", "failed"].includes(data.status)) { setStatus("error"); return; }
        if (polls.current++ < 8) setTimeout(poll, 2000); else setStatus("timeout");
      } catch { setStatus("error"); }
    };
    poll();
  }, [params, clear, refresh]);

  return (
    <Layout>
      <div className="max-w-lg mx-auto px-5 py-32 text-center">
        {status === "checking" && <><Loader2 size={48} className="text-eon-gold mx-auto animate-spin" /><h1 className="eon-heading text-3xl uppercase mt-6">Confirming Payment</h1><p className="text-eon-muted mt-3">Please wait a moment...</p></>}
        {status === "paid" && <><CheckCircle2 size={56} className="text-eon-gold mx-auto" data-testid="payment-success-icon" /><h1 className="eon-heading text-4xl uppercase mt-6">{kind === "deposit" ? "Reservation Confirmed" : "Payment Confirmed"}</h1>
          <p className="text-eon-text/80 mt-3">{kind === "membership" ? "Your membership is now active. Welcome to the inner circle." : kind === "deposit" ? "Your reservation deposit is secured and your puppy is now reserved. Our team will be in touch with next steps." : "Thank you for your order. We're preparing it now."}</p>
          <p className="text-eon-muted text-sm mt-3" data-testid="payment-receipt-note">A confirmation and receipt have been emailed to you.</p>
          {downloads.length > 0 && (
            <div className="mt-6 flex flex-col items-center gap-3" data-testid="downloads-list">
              {downloads.map((d, i) => (
                <a key={i} href={d.url} target="_blank" rel="noreferrer" download data-testid={`download-product-btn-${i}`}
                  className="inline-block bg-eon-gold text-white text-sm font-bold uppercase tracking-wider px-8 py-4 hover:bg-eon-goldhover transition-colors">
                  ⬇ Download {d.name || "Your Purchase"}
                </a>
              ))}
            </div>
          )}
          <div className="flex gap-3 justify-center mt-8"><Link to="/dashboard" className="bg-eon-gold text-white text-sm font-bold uppercase tracking-wider px-6 py-3 hover:bg-eon-goldhover transition-colors">Go to Dashboard</Link><Link to="/" className="border border-eon-border text-eon-text text-sm font-bold uppercase tracking-wider px-6 py-3 hover:border-eon-gold transition-colors">Home</Link></div></>}
        {(status === "error" || status === "timeout") && <><XCircle size={56} className="text-red-400 mx-auto" /><h1 className="eon-heading text-3xl uppercase mt-6">{status === "timeout" ? "Still Processing" : "Something Went Wrong"}</h1><p className="text-eon-muted mt-3">{status === "timeout" ? "Your payment is taking longer than expected. Check your dashboard shortly." : "We couldn't confirm this payment."}</p><Link to="/dashboard" className="inline-block mt-8 border border-eon-border px-6 py-3 text-sm hover:border-eon-gold transition-colors">Dashboard</Link></>}
      </div>
    </Layout>
  );
}
